import React, { useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { FaFileUpload, FaDownload, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';

const UploadTradersView = ({ userInfo, API_BASE_URL, auth }) => {
  // Add CSS animation for spinner
  const spinnerStyle = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  // Inject CSS if not already present
  React.useEffect(() => {
    const styleId = 'upload-spinner-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = spinnerStyle;
      document.head.appendChild(style);
    }
  }, []);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
    percentage: 0
  });
  const [notification, setNotification] = useState({
    show: false,
    type: '', // 'success' or 'error'
    title: '',
    message: ''
  });

  const downloadTemplate = () => {
    try {
      // Sample data for template
      const sampleData = [
        {
          'Business Name': 'Sample Shop',
          'Owner Name': 'John Doe',
          'Phone': '0241234567',
          'Email': 'john@example.com',
          'Business Type': 'Retail',
          'Location': 'Market Street, Accra',
          'Registration Date': '2024-01-15',
          'Status': 'active'
        },
        {
          'Business Name': 'Example Store',
          'Owner Name': 'Jane Smith',
          'Phone': '0207654321',
          'Email': 'jane@example.com',
          'Business Type': 'Wholesale',
          'Location': 'Commercial Area, Kumasi',
          'Registration Date': '2024-01-20',
          'Status': 'active'
        }
      ];

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(sampleData);

      // Set column widths
      const columnWidths = [
        { wch: 25 }, // Business Name
        { wch: 20 }, // Owner Name
        { wch: 15 }, // Phone
        { wch: 25 }, // Email
        { wch: 15 }, // Business Type
        { wch: 30 }, // Location
        { wch: 15 }, // Registration Date
        { wch: 10 }, // Status
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Traders Template');

      // Generate filename with current date
      const filename = `traders_upload_template_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

      // Show success notification
      setNotification({
        show: true,
        type: 'success',
        title: 'Template Downloaded',
        message: `Excel template downloaded successfully as ${filename}`
      });

    } catch (error) {
      console.error('Error generating template:', error);
      setNotification({
        show: true,
        type: 'error',
        title: 'Download Failed',
        message: 'Failed to generate Excel template. Please try again.'
      });
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
     
      if (allowedTypes.includes(selectedFile.type)) {
        setFile(selectedFile);
        setUploadResult(null);
      } else {
        setNotification({
          show: true,
          type: 'error',
          title: 'Invalid File Type',
          message: 'Please select a valid Excel file (.xlsx or .xls)'
        });
        e.target.value = '';
      }
    }
  };

  const uploadFile = async () => {
    if (!file) {
      setNotification({
        show: true,
        type: 'error',
        title: 'No File Selected',
        message: 'Please select an Excel file first'
      });
      return;
    }

    // Validate zone assignment
    if (!userInfo.zone_id) {
      setNotification({
        show: true,
        type: 'error',
        title: 'Zone Assignment Required',
        message: 'You must be assigned to a zone to upload traders. Please contact your administrator.'
      });
      return;
    }

    setUploading(true);
    const startTime = Date.now();

    try {
      // Read Excel file
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        throw new Error('Excel file is empty or has no valid data');
      }

      const results = {
        successful: 0,
        errors: [],
        total: jsonData.length
      };

      // Initialize progress
      setUploadProgress({
        current: 0,
        total: jsonData.length,
        percentage: 0
      });

      // Show progress notification
      setNotification({
        show: true,
        type: 'info',
        title: 'Processing Upload',
        message: `Processing ${jsonData.length} traders. Please wait...`
      });

      // Process in batches for better performance
      const batchSize = 10; // Process 10 traders at a time
      const batches = [];
      for (let i = 0; i < jsonData.length; i += batchSize) {
        batches.push(jsonData.slice(i, i + batchSize));
      }

      console.log(`Processing ${jsonData.length} traders in ${batches.length} batches of ${batchSize}`);

      // Process each batch
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        console.log(`Processing batch ${batchIndex + 1}/${batches.length}`);

        // Update progress
        setNotification({
          show: true,
          type: 'info',
          title: 'Processing Upload',
          message: `Processing batch ${batchIndex + 1} of ${batches.length}...`
        });

        // Process each row in the batch
        const batchPromises = batch.map(async (row, rowIndex) => {
          const globalIndex = batchIndex * batchSize + rowIndex;
          try {
            // Clean and validate data
            const businessName = String(row['Business Name'] || '').trim();
            const ownerName = String(row['Owner Name'] || '').trim();
            const phone = String(row['Phone'] || '').trim();
            const email = String(row['Email'] || '').trim();
            const businessType = String(row['Business Type'] || 'General').trim();
            const location = String(row['Location'] || '').trim();
            const status = String(row['Status'] || 'active').trim().toLowerCase();

          // Validate required fields
          if (!businessName) {
            throw new Error('Business Name is required');
          }
          if (!ownerName) {
            throw new Error('Owner Name is required');
          }

          // Validate userInfo data
          if (!userInfo.mmda_id || !userInfo.zone_id || !userInfo.user_id) {
            throw new Error('User information is incomplete. Please refresh and try again.');
          }

          // Parse and validate registration date
          let registrationDate = new Date().toISOString().split('T')[0];
          if (row['Registration Date']) {
            const dateStr = String(row['Registration Date']).trim();
            if (dateStr) {
              // Try different date formats
              let parsedDate;
              if (dateStr.includes('/')) {
                // Handle MM/DD/YYYY or DD/MM/YYYY format
                const parts = dateStr.split('/');
                if (parts.length === 3) {
                  // Assume MM/DD/YYYY format
                  parsedDate = new Date(parts[2], parts[0] - 1, parts[1]);
                }
              } else if (dateStr.includes('-')) {
                // Handle YYYY-MM-DD format
                parsedDate = new Date(dateStr);
              } else {
                // Try direct parsing
                parsedDate = new Date(dateStr);
              }

              if (parsedDate && !isNaN(parsedDate.getTime())) {
                registrationDate = parsedDate.toISOString().split('T')[0];
              }
            }
          }

          // Validate phone format (basic validation)
          if (phone && !/^[0-9+\-\s()]+$/.test(phone)) {
            throw new Error('Invalid phone number format');
          }

          // Validate email format (basic validation)
          if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            throw new Error('Invalid email format');
          }

          // Validate and convert numeric IDs
          const mmdaId = parseInt(userInfo.mmda_id);
          const zoneId = parseInt(userInfo.zone_id);
          const createdBy = parseInt(userInfo.user_id);

          if (isNaN(mmdaId) || isNaN(zoneId) || isNaN(createdBy)) {
            throw new Error('Invalid user ID information. Please refresh and try again.');
          }

          // Ensure phone number has country code if provided
          const phoneWithCode = phone && !phone.startsWith('+233')
            ? `+233${phone}`
            : phone;

          const traderData = {
            business_name: businessName,
            owner_name: ownerName,
            phone: phoneWithCode || null,
            email: email || null,
            business_type: businessType,
            location: location || null,
            registration_date: registrationDate,
            status: status === 'inactive' ? 'inactive' : 'active',
            mmda_id: mmdaId,
            zone_id: zoneId,
            created_by: createdBy,
            group_id: null // Set to null for now, can be updated later
          };

          console.log('Sending trader data:', traderData); // Debug log

            // Send individual trader to backend
            const response = await axios.post(`${API_BASE_URL}/traders`, traderData, auth);
            console.log(`Trader ${globalIndex + 1} created successfully:`, response.data);
            return { success: true, index: globalIndex };
          } catch (error) {
            console.error(`Error processing row ${globalIndex + 2}:`, error);

            let errorMessage = 'Unknown error occurred';
            if (error.response) {
              errorMessage = error.response.data?.error ||
                            error.response.data?.message ||
                            `Server error: ${error.response.status}`;
            } else if (error.request) {
              errorMessage = 'Network error: Unable to reach server';
            } else {
              errorMessage = error.message;
            }

            return {
              success: false,
              index: globalIndex,
              error: errorMessage,
              row: globalIndex + 2,
              data: row
            };
          }
        });

        // Wait for all traders in this batch to complete
        const batchResults = await Promise.allSettled(batchPromises);

        // Process batch results
        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            if (result.value.success) {
              results.successful++;
            } else {
              results.errors.push({
                row: result.value.row,
                error: result.value.error,
                data: result.value.data
              });
            }
          } else {
            // Promise was rejected
            const globalIndex = batchIndex * batchSize + index;
            results.errors.push({
              row: globalIndex + 2,
              error: result.reason?.message || 'Promise rejected',
              data: batch[index]
            });
          }
        });

        // Update progress
        const processedCount = (batchIndex + 1) * batchSize;
        const currentProgress = Math.min(processedCount, jsonData.length);
        const percentage = Math.round((currentProgress / jsonData.length) * 100);

        setUploadProgress({
          current: currentProgress,
          total: jsonData.length,
          percentage: percentage
        });

        // Small delay between batches to prevent overwhelming the server
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Calculate processing time
      const endTime = Date.now();
      const processingTimeMs = endTime - startTime;
      const processingTimeSeconds = (processingTimeMs / 1000).toFixed(1);

      // Add processing time to results
      results.processingTime = `${processingTimeSeconds}s`;

      setUploadResult(results);
      setFile(null);
      document.getElementById('file-input').value = '';

      // Show success notification
      const successCount = results.successful || 0;
      const errorCount = results.errors ? results.errors.length : 0;

      setNotification({
        show: true,
        type: 'success',
        title: 'Upload Completed',
        message: `Successfully uploaded ${successCount} traders. ${errorCount > 0 ? `${errorCount} errors occurred.` : ''}`
      });
    } catch (error) {
      console.error('Error uploading file:', error);

      // Show error notification
      let errorMessage = 'Failed to upload file. Please try again.';

      if (error.message.includes('Excel file is empty')) {
        errorMessage = 'The Excel file is empty or contains no valid data. Please check your file and try again.';
      } else if (error.message.includes('not a valid Excel file')) {
        errorMessage = 'The selected file is not a valid Excel file. Please select a .xlsx or .xls file.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setNotification({
        show: true,
        type: 'error',
        title: 'Upload Failed',
        message: errorMessage
      });
    } finally {
      setUploading(false);
    }
  };

  const handleNotificationClose = () => {
    setNotification({
      show: false,
      type: '',
      title: '',
      message: ''
    });
  };

  return (
    <div style={{ padding: '1rem', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ margin: '0 0 2rem 0', color: '#2c3e50' }}>
        <FaFileUpload style={{ marginRight: '0.5rem' }} />
        Upload Traders via Excel
      </h1>

      {/* Instructions */}
      <div style={{
        backgroundColor: '#e8f4fd',
        padding: '1.5rem',
        borderRadius: '0.5rem',
        marginBottom: '2rem',
        border: '1px solid #3498db'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>Instructions:</h3>
        <ol style={{ margin: 0, paddingLeft: '1.5rem', color: '#7f8c8d' }}>
          <li>Download the Excel template using the button below</li>
          <li>Fill in the trader information in the template</li>
          <li>Save the file and upload it using the upload section</li>
          <li>Review the upload results and fix any errors if needed</li>
        </ol>
      </div>

      {/* Download Template */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>Step 1: Download Template</h3>
        <p style={{ margin: '0 0 1.5rem 0', color: '#7f8c8d' }}>
          Download the Excel template with the correct format and sample data.
        </p>
        <button
          onClick={downloadTemplate}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '1rem',
            fontWeight: '500'
          }}
        >
          <FaDownload />
          Download Excel Template
        </button>
      </div>

      {/* Upload File */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>Step 2: Upload Filled Template</h3>
        <p style={{ margin: '0 0 1.5rem 0', color: '#7f8c8d' }}>
          Select your completed Excel file and upload it to add traders in bulk.
                 </p>
                 <span style={{ color: '#d35400', fontSize: '0.75rem', marginTop: '0.2rem', textAlign: 'center', maxWidth: 140 }}>
              <b>Note:</b> Don&apos;t add 0 in front of the phone number column
            </span>

       
        <div style={{ marginBottom: '1.5rem' }}>
          <input
            id="file-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            style={{
              padding: '0.75rem',
              border: '2px dashed #ddd',
              borderRadius: '0.5rem',
              width: '100%',
              fontSize: '1rem'
            }}
          />
        </div>

        {file && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ margin: 0, color: '#2c3e50' }}>
              Selected file: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
            </p>
          </div>
        )}

        {/* Progress Bar */}
        {uploading && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1.5rem',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem',
            border: '1px solid #e9ecef'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem'
            }}>
              <span style={{ color: '#2c3e50', fontWeight: '500' }}>
                Upload Progress
              </span>
              <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                {uploadProgress.current} / {uploadProgress.total} traders ({uploadProgress.percentage}%)
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${uploadProgress.percentage}%`,
                height: '100%',
                backgroundColor: '#28a745',
                borderRadius: '4px',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{
              marginTop: '0.5rem',
              fontSize: '0.85rem',
              color: '#6c757d',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
              Processing traders in batches...
            </div>
          </div>
        )}

        <button
          onClick={uploadFile}
          disabled={!file || uploading}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: !file || uploading ? '#95a5a6' : '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: !file || uploading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '1rem',
            fontWeight: '500'
          }}
        >
          {uploading ? <FaSpinner className="spin" /> : <FaFileUpload />}
          {uploading ? 'Uploading...' : 'Upload Traders'}
        </button>
      </div>

      {/* Upload Results */}
      {uploadResult && (
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>Upload Results</h3>
         
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              textAlign: 'center',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '0.5rem'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#3498db' }}>
                {uploadResult?.total || 0}
              </h4>
              <p style={{ margin: 0, color: '#7f8c8d' }}>Total Records</p>
            </div>

            <div style={{
              textAlign: 'center',
              padding: '1rem',
              backgroundColor: '#d4edda',
              borderRadius: '0.5rem'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#27ae60' }}>
                {uploadResult?.successful || 0}
              </h4>
              <p style={{ margin: 0, color: '#155724' }}>Successful</p>
            </div>
           
            <div style={{
              textAlign: 'center',
              padding: '1rem',
              backgroundColor: '#f8d7da',
              borderRadius: '0.5rem'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#e74c3c' }}>
                {uploadResult?.errors?.length || 0}
              </h4>
              <p style={{ margin: 0, color: '#721c24' }}>Failed</p>
            </div>
          </div>

          {uploadResult?.errors?.length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 1rem 0', color: '#e74c3c' }}>Errors Found:</h4>
              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '0.5rem'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                        Row
                      </th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                        Error
                      </th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                        Data
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult?.errors?.map((error, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '0.75rem' }}>{error.row}</td>
                        <td style={{ padding: '0.75rem', color: '#e74c3c' }}>{error.error}</td>
                        <td style={{ padding: '0.75rem', fontSize: '0.8rem', color: '#7f8c8d' }}>
                          {JSON.stringify(error.data).substring(0, 100)}...
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notification Modal */}
      {notification.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            textAlign: 'center'
          }}>
            {/* Icon */}
            <div style={{
              marginBottom: '1rem',
              fontSize: '3rem'
            }}>
              {notification.type === 'success' ? '✅' : '❌'}
            </div>

            {/* Title */}
            <h3 style={{
              margin: '0 0 1rem 0',
              color: notification.type === 'success' ? '#27ae60' : '#e74c3c',
              fontSize: '1.25rem',
              fontWeight: 'bold'
            }}>
              {notification.title}
            </h3>

            {/* Message */}
            <p style={{
              margin: '0 0 2rem 0',
              color: '#666',
              fontSize: '1rem',
              lineHeight: '1.5'
            }}>
              {notification.message}
            </p>

            {/* Close Button */}
            <button
              onClick={handleNotificationClose}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: notification.type === 'success' ? '#27ae60' : '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                minWidth: '100px'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadTradersView;